import React, { useState } from 'react';

import { Button, Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faTrash, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Swal from 'sweetalert2';
import SwalDialog from 'lib/utils/toast';
import { useParams } from 'react-router-dom';

import ActionDropDown from 'components/shared/ActionDropDown';
import { areYouSure } from 'lib/utils/toast';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import {
  FILE_EXPORTS_GENERATE_REPORTS,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  USER_TIME_LOG_DELETE,
  USER_TIME_LOG_GET_LOGS,
  USERS_GET_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import PaymentReportExportModal from 'components/settings/user_time_logs/PaymentReportExportModal';
import pointerOnHover from 'lib/css/pointerOnHover';
import { ReportType } from 'lib/enums/ReportGeneratrionEnums';
import { salmon } from 'lib/css/colors';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useDelete from 'lib/hooks/useDelete';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import UserTimeLogModal from 'components/settings/user_time_logs/UserTimeLogModal';
import UserTimeLogsConfigModal from 'components/settings/user_time_logs/UserTimeLogsConfigModal';
import {
  UserTimeLogActivityTypeOptions,
  UserTimeLogActivityTypeDisplayOptions,
} from 'components/settings/user_time_logs/UserTimeLogEnums';

function UserTimeLogView() {
  const hasUserPermissions = useUserPermissions();

  const { Usertype } = useParams();

  const [selectedVendor, setSelectedVendor] = useState({});
  const [selectedManufacturer, setSelectedManufacturer] = useState({});
  const [selectedActivityType, setSelectedActivityType] = useState({});
  const [selectedUser, setSelectedUser] = useState({});
  const [showAddTimeModal, setShowAddTimeModal] = useState(false);
  const [showPaymentReportExportModal, setShowPaymentReportExportModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [reportType, setReportType] = useState('');
  const [showUserTimeLogsConfigModal, setShowUserTimeLogsConfigModal] = useState(false);

  const adminView = Usertype === 'admin' && hasUserPermissions([UserPermission.ADMIN_OPERATIONS]);
  const canManagePaymentInfo = hasUserPermissions([UserPermission.MANAGE_PAYMENTS]);

  const {
    data: { logs, total_time } = {},
    refetch,
    loading,
  } = useGet(USER_TIME_LOG_GET_LOGS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    global_vendor_id: selectedVendor?.value || null,
    global_manufacturer_id: selectedManufacturer?.value || null,
    activity_type: selectedActivityType?.value || null,
    start_date: startDate || null,
    end_date: endDate || null,
    admin: adminView,
    selected_user_id: selectedUser?.value || null,
  });

  const { data: { count } = {}, refetch: refetchCount } = useGet(USER_TIME_LOG_GET_LOGS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    global_vendor_id: selectedVendor?.value || null,
    global_manufacturer_id: selectedManufacturer?.value || null,
    activity_type: selectedActivityType?.value || null,
    start_date: startDate || null,
    end_date: endDate || null,
    admin: adminView,
    selected_user_id: selectedUser?.value || null,
    count_only: true,
  });

  const { deleteData: deleteUserTimeLog } = useDelete(
    USER_TIME_LOG_DELETE,
    false,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Deleted successfully',
        title: 'Success',
        position: 'center',
      });
      refetch();
      refetchCount();
    },
    () => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred',
      });
    },
  );

  const { postData: generateReport } = usePost(
    FILE_EXPORTS_GENERATE_REPORTS,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () => {
      SwalDialog('error', 'An error occurred while Generationg the File', 'Error', 'center');
    },
  );

  const handleGenerateReport = () => {
    const fromDate = new Date(startDate);
    const toDate = new Date(endDate);

    if (fromDate > toDate) {
      SwalDialog(
        'error',
        '"From" date cannot be later than the "To" date',
        'Invalid Date Range',
        'center',
      );
      return;
    }
    generateReport({
      from: startDate,
      to: endDate,
      report_user_id: selectedUser?.value || null,
      report_type: ReportType.USER_TIME_LOG_REPORT,
      activity_type: selectedActivityType?.value || null,
      global_vendor_id: selectedVendor?.value || null,
      global_manufacturer_id: selectedManufacturer?.value || null,
    });
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
    ).padStart(2, '0')}`;
  };

  const formatTimeSpent = seconds => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };
  return (
    <Container className={css(styles.container)}>
      <Row className="ml-3 mr-1">
        <div className="mb-3 d-flex justify-content-between align-items-center w-100">
          <h3 className="font-weight-bold">{adminView ? 'User time logs' : 'Track Your Time'}</h3>
          {adminView ? (
            <div className="d-flex align-items-center">
              {canManagePaymentInfo && (
                <OverlayTrigger overlay={<Tooltip>Configure User Time Logs</Tooltip>}>
                  <FontAwesomeIcon
                    icon={faCog}
                    className={classNames(pointerOnHover, 'mr-2')}
                    onClick={() => setShowUserTimeLogsConfigModal(true)}
                  />
                </OverlayTrigger>
              )}
              <ActionDropDown
                label="Export Reports"
                submenuItems={[
                  {
                    title: 'Generate Time Log Report',
                    action: () => handleGenerateReport(),
                  },
                  ...(canManagePaymentInfo
                    ? [
                        {
                          title: 'Export Weekly User Throughput Report',
                          action: () => {
                            setReportType(ReportType.USER_THROUGHPUT_REPORT);
                            setShowPaymentReportExportModal(true);
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          ) : (
            <div>
              <Button
                className="mr-2"
                variant="primary"
                style={{ height: '30px' }}
                onClick={() => setShowAddTimeModal(true)}
              >
                Add Time
              </Button>
              <Button
                variant="primary"
                style={{ height: '30px' }}
                onClick={() => {
                  generateReport({ report_type: ReportType.USER_INVOICE_REPORT });
                }}
              >
                Export Invoice
              </Button>
            </div>
          )}
        </div>
      </Row>
      <Row className="ml-1">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Activity Type</Form.Label>
            <Select
              options={UserTimeLogActivityTypeOptions}
              value={selectedActivityType}
              onChange={option => setSelectedActivityType(option)}
              isClearable
            />
          </Form.Group>
        </Col>
        {adminView && (
          <Col md={3}>
            <Form.Group>
              <Form.Label>User</Form.Label>
              <SearchableScrollableSelect
                initOption={selectedUser}
                onChange={option => {
                  setSelectedUser(option);
                }}
                fetchUrl={USERS_GET_FOR_DROPDOWN}
                optionMapper={user => ({ label: user.name, value: user.id })}
                placeholder="Select user"
                autoFocus={false}
              />
            </Form.Group>
          </Col>
        )}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Distributor</Form.Label>
            <SearchableScrollableSelect
              initOption={selectedVendor}
              onChange={option => setSelectedVendor(option)}
              fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
              optionMapper={vendor => ({ label: vendor.name, value: vendor.id })}
              autoFocus={false}
              disabled={selectedManufacturer?.value}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Manufacturer</Form.Label>
            <SearchableScrollableSelect
              initOption={selectedManufacturer}
              onChange={option => setSelectedManufacturer(option)}
              fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
              optionMapper={manufacturer => ({ label: manufacturer.name, value: manufacturer.id })}
              autoFocus={false}
              disabled={selectedVendor?.value}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Row>
            <Col>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col className="d-flex justify-content-end align-items-end mr-1 mb-3">
          {adminView && (
            <div className="font-weight-bold">Total Hours Spent: {formatTimeSpent(total_time)}</div>
          )}
        </Col>
      </Row>
      <Row>
        <Col
          className={classNames(
            css(styles.pagination),
            'd-flex justify-content-between align-items-center ml-4',
          )}
        >
          <div className="text-muted">
            {count > 0
              ? `Showing ${itemsPerPage * (currentPage - 1) + 1}-${Math.min(
                  itemsPerPage * currentPage,
                  count,
                )} of ${count} logs`
              : 'No logs found'}
          </div>
          <PaginationComponent
            totalItems={count}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            defaultSize
          />
        </Col>
      </Row>
      <Row className="ml-3 mr-1">
        {loading ? (
          <LoadingSpinner />
        ) : !adminView ? (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type of Work</th>
                <th>Distributor/Manufacturer</th>
                <th>Note</th>
                <th>Output</th>
                <th>Hours Spent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs?.map(log => (
                <tr key={log.id}>
                  <td>{formatDate(log.date)}</td>
                  <td>{UserTimeLogActivityTypeDisplayOptions[log.activity_type]}</td>
                  <td>{log.global_vendor?.name || log.global_manufacturer?.name || '-'}</td>
                  <td>{log.description || '-'}</td>
                  <td>{log.output || '-'}</td>
                  <td>{formatTimeSpent(log.time_spent)}</td>
                  <td>
                    {!adminView && (
                      <OverlayTrigger overlay={<Tooltip>Delete time log</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faTrash}
                          className={classNames(pointerOnHover)}
                          onClick={() => {
                            areYouSure(
                              () => deleteUserTimeLog({ log_id: log.id }),
                              'Are you sure you want to delete this log?',
                            );
                          }}
                          color={salmon}
                        />
                      </OverlayTrigger>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Type of Work</th>
                <th>Distributor/Manufacturer</th>
                <th>Note</th>
                <th>Output</th>
                <th>Hours Spent</th>
              </tr>
            </thead>
            <tbody>
              {logs?.map(log => (
                <tr key={log.id}>
                  <td>{formatDate(log.date)}</td>
                  <td>{log.user.name}</td>
                  <td>{UserTimeLogActivityTypeDisplayOptions[log.activity_type]}</td>
                  <td>{log.global_vendor?.name || log.global_manufacturer?.name || '-'}</td>
                  <td>{log.description || '-'}</td>
                  <td>{log.output || '-'}</td>
                  <td>{formatTimeSpent(log.time_spent)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Row>
      {showAddTimeModal && (
        <UserTimeLogModal
          onHide={shouldRefetch => {
            setShowAddTimeModal(false);
            if (shouldRefetch) {
              refetch();
              refetchCount();
            }
          }}
        />
      )}
      {showPaymentReportExportModal && (
        <PaymentReportExportModal
          reportType={reportType}
          onHide={() => setShowPaymentReportExportModal(false)}
        />
      )}
      {showUserTimeLogsConfigModal && (
        <UserTimeLogsConfigModal onHide={() => setShowUserTimeLogsConfigModal(false)} />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
  },
});

export default UserTimeLogView;
